import { type ElementProps, type JsxChild, jsx } from '@amazon/vinyl-tsx'
import { data } from '@amazon/vinyl-observable'
import {
    browseMusicCollections,
    type BrowseMusicCollectionsOptions,
    type BrowseMusicCollectionsResult,
} from '@/browse/browseMusicCollectionsService'
import { RecommendationsShoveler } from '@/browse/RecommendationsShoveler'
import {
    type RecommendationRendererDeps,
    toRecommendationModel,
} from '@/browse/RecommendationRenderer'
import { AsyncContainer } from '@/components/AsyncContainer'
import { ctx } from '@/util/context'
import { Abort, logDebug, logWarn, noop } from '@amazon/vinyl-util'
import { doWhenIdle } from '@/util/idleQueue'
import { ImageSizes } from '@/style/styleCommon'
import { Link } from '@/components/Link'
import { widgetIdFromRecommendationId } from '@/seeAll/seeAllService'
import { getString } from '@/browse/i18n/bundle.generated'

export type BrowseMusicCollectionsDeps = RecommendationRendererDeps

export interface BrowseMusicCollectionsProps extends ElementProps<'div'> {
    readonly data: Pick<BrowseMusicCollectionsOptions, 'limit' | 'itemLimit' | 'type' | 'targets'>
    readonly deps: BrowseMusicCollectionsDeps
    /**
     * Optional content rendered when the gateway returns zero
     * recommendation rows for this collection type. Surfaces an
     * empty state so the user sees feedback instead of a blank
     * page (the videos collection in particular comes back empty
     * for some device types).
     */
    readonly emptyState?: JsxChild
}

const { target } = ctx('browse')
const MAX_PAGES = 20

export function BrowseMusicCollections(props: BrowseMusicCollectionsProps) {
    // Tied to the gateway response, NOT the number of rendered
    // shoveler rows: some edges may filter out client-side (e.g.
    // empty `recommendedContent`) yet the gateway didn't actually
    // return an empty collection. We only flip emptyState on when
    // the gateway itself sent zero edges.
    const isEmpty = data(false)

    const dataGenerator = async function* () {
        let hasNextPage = true
        let cursor: string | null = null
        let i = 0
        while (hasNextPage) {
            const result = await browseMusicCollections({
                ...props.data,
                cursor,
            })
            yield result
            const pageInfo = result.browse?.recommendationConnection?.pageInfo
            hasNextPage = ++i < MAX_PAGES && (pageInfo?.hasNextPage ?? false)
            cursor = pageInfo?.token ?? null
        }
    }
    const viewGenerator = async function* () {
        let edgeCount = 0
        for await (const page of dataGenerator()) {
            edgeCount += page.browse?.recommendationConnection?.edges.length ?? 0
            yield* createShovelerViews(page, props.deps)
        }
        if (edgeCount === 0) isEmpty.value = true
    }

    return (
        <div
            className="musicCollections"
            {...props}
            onConnect={() => {
                const abort = new Abort()
                doWhenIdle(async () => {
                    logDebug(target, 'refreshing browse music data')
                    for await (const _ of dataGenerator()) {
                        abort.throwIfAborted()
                    }
                }).catch(noop)
                return () => abort.abort()
            }}
        >
            <AsyncContainer
                data={viewGenerator()}
                intersection={{
                    rootMargin: '2500px',
                }}
            />
            {props.emptyState && <div visible={isEmpty}>{props.emptyState}</div>}
        </div>
    )
}

function createShovelerViews(
    result: BrowseMusicCollectionsResult,
    deps: RecommendationRendererDeps
): HTMLElement[] {
    const edges = result.browse?.recommendationConnection?.edges
    if (!edges) {
        // Anonymous browsing legitimately returns null for some
        // collection types — surface at warn without an error toast.
        logWarn(target, 'browse music collection returned empty data')
        return []
    }

    const out: HTMLElement[] = []
    for (const edge of edges) {
        if (!edge.node.itemType) continue
        const rowItemType = edge.node.itemType
        const useSmall = rowItemType === 'TRACK'
        const data =
            edge.node.recommendedContent?.edges
                .map((edge) => toRecommendationModel(edge.node, { rowItemType }))
                .filter((e) => e != null) ?? []
        if (data.length === 0) continue
        // A row of videos uses a 16:9 card instead of the square audio
        // card. Detect by the row's itemType — Firefly returns music
        // videos as Track typenames, so checking the model's type alone
        // isn't reliable on a fresh response.
        const isVideoRow = rowItemType === 'VIDEO'
        const sizeClassName = useSmall ? 'small' : isVideoRow ? 'large video' : 'large'
        const imageSize = useSmall ? ImageSizes.SMALL : ImageSizes.LARGE
        const widgetId = widgetIdFromRecommendationId(edge.node.id)
        const seeAllHref = widgetId
            ? `/browse/music-items/home/recommendation/${encodeURIComponent(widgetId)}`
            : null
        out.push(
            <div className="recommendationContainer">
                <RecommendationsShoveler
                    className={`${sizeClassName} recommendations`}
                    headerTitle={edge.node.name ?? ''}
                    header={
                        <div className="rowHeader">
                            <h2>{edge.node.name}</h2>
                            {seeAllHref && (
                                <Link className="seeAllLink" href={seeAllHref}>
                                    {getString('dmusic_bauhaus_text_see_more')}
                                </Link>
                            )}
                        </div>
                    }
                    imageSize={imageSize}
                    data={data}
                    deps={deps}
                />
            </div>
        )
    }
    return out
}
